<template>
  <div class="reset-password-container">
    <div v-if="loading" class="overlay">
      <loading-icon></loading-icon>
    </div>
    <div v-else class="reset-password-container">
      <div v-for="step in steps" :key="step.name" class="step.name">
        <div v-if="currentStep === step.name">
          <login-step
              :linkLabel="step.linkLabel"
              :linkText="step.linkText"
              :stepLabel="step.stepLabel"
              :hasBeenSent="step.hasBeenSent"
              @linkClicked="linkAction"
              @submit="submitAction">
            <div slot="input" v-for="input in step.inputs" :key="input.id">
              <number-input
                  :autocomplete="input.autocomplete"
                  :fieldToUpdate="input.fieldToUpdate"
                  :id="input.id"
                  :inputLabel="input.inputLabel"
                  :requiredLength="input.requiredLength"
                  :type="input.type"
                  @updateNumber="updateData">
              </number-input>
            </div>
          </login-step>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import AxiosMixin from '@/mixins/AxiosMixin'
  import LoadingIcon from '@/components/Shared/LoadingIcon'
  import LoginStep from '@/components/Login/LoginStep'
  import NumberInput from '@/components/Shared/NumberInput'

  export default {
    props: {
      phoneNumber: String,
      baseUrl: String,
    },

    name: 'ResetPasword',

    components: {
      LoadingIcon,
      LoginStep,
      NumberInput,
    },

    mixins: [AxiosMixin],

    data() {
      return {
        currentStep: 'pin',
        loading: null,
        pageTimer: null,
        password: null,
        passwordConfirm: null,
        pin: null,
        pinId: null,
        steps: []
      }
    },

    methods: {
      afterCreatePin(response) {
        const id = response.data?.member_access_code_id
        if (id) {
          this.pinId = id
        } else {
          this.resetLogin()
          this.$emit('setErrors')
        }
        this.loading = false
      },

      afterResendPinError(error) {
        if (error.response?.status === 401) {
          this.$emit('setPageAlert', 'access PIN expired, please start again')
          this.resetLogin()
        } else {
          this.$emit('setErrors', error)
        }
      },

      afterResendPinSuccess(response) {
        const pinId = response.data?.member_access_code_id
        pinId ? this.$emit('setPageAlert', 'access PIN resent') : this.$emit('setErrors')
      },

      afterSetPasswordError(error) {
        if (error.response?.status === 401) {
          this.resetLogin()
          this.$emit('setPageAlert', 'expired session, please start again')
          this.loading = false
        } else if (error.response?.status === 403) {
          this.$emit('setPageAlert', error.response.data.error)
          this.loading = false
        } else {
          this.$emit('setErrors', error)
        }
      },

      afterSetPasswordSuccess(response) {
        const data = response?.data,
            signatureNeeded = data?.signature_needed

        if (data && !signatureNeeded) {
          this.redirectTo(data?.wallet_url)
        } else if (data && signatureNeeded) {
          this.$emit('updateData', 'allowedSmsStored', data?.allowed_sms)
          this.$emit('updateData', 'type', 'signature-capture')
          this.$emit('setPageAlert')
        } else {
          this.$emit('setErrors')
        }
      },

      redirectTo(redirectUrl) {
        // this method is used for subscription sharing links routing

        if (location.search) {
          const urlParams = location.search.substr(1).split('&')
          let parsedParams = urlParams.reduce((acc, param) => {
            let splitParams = param.split('=')
            acc[splitParams[0]] = unescape(splitParams[1])
            return acc
          }, {})

          // populate params for sharing links
          if (parsedParams['plan']) {
            redirectUrl = redirectUrl.replace(/\?/, `?plan=${parsedParams['plan']}&`)
          }
          if (parsedParams['link']) {
            redirectUrl = redirectUrl.replace(/\?/, `?link=${parsedParams['link']}&`)
          }
          if (parsedParams['route']) {
            redirectUrl = redirectUrl.replace(/\/profile/, `${parsedParams['route']}`)
          }

        }
        this.loading = false
        window.location.href = redirectUrl
      },

      redirectToJoinForm() {
        this.loading = false
        window.location.href = this.joinLink
      },

      afterSubmitPinError(error) {
        if (error.response?.status === 401) {
          this.$emit('setPageAlert', 'invalid or expired access PIN')
          this.loading = false
        } else {
          this.$emit('setErrors', error)
        }
      },

      afterSubmitPinSuccess(response) {
        if (response.status === 202) {
          this.currentStep = 'set-password'
          this.$emit('setPageAlert')
          clearInterval(this.pageTimer)
        } else {
          this.$emit('setErrors')
        }
        this.pin = null
        this.loading = false
      },

      configureSteps() {
        this.steps = [
          {
            linkLabel: "didn't get the PIN?",
            linkText: "resend",
            name: 'pin',
            stepLabel: "a temporary PIN has been sent to your phone/email - please enter it below to proceed",
            hasBeenSent: false,
            inputs: [{
              autocomplete: "one-time-code",
              fieldToUpdate: "pin",
              id: "pin",
              requiredLength: 6
            }]
          },
          {
            name: 'set-password',
            stepLabel: "create and confirm a 4 character log in code (should only contain numbers) for secure access to your wallet",
            inputs: [
              {
                fieldToUpdate: "password",
                id: "password",
                inputLabel: 'log in code',
                requiredLength: 4,
              },
              {
                fieldToUpdate: "passwordConfirm",
                id: "password-confirm",
                inputLabel: 'confirm log in code',
                requiredLength: 4,
              }
            ]
          },
        ]
      },

      confirmNewPassword() {
        if (!this.password || !this.passwordConfirm) {
          this.$emit('setPageAlert', 'both fields must be complete')
        } else if (this.password !== this.passwordConfirm) {
          this.$emit('setPageAlert', 'PINs do not match')
        } else {
          this.setPassword()
          this.passwordConfirm = null
        }
      },

      createPin() {
        this.axiosMethod({
          methodType: 'post',
          url: `${this.pinUrl}`,
          params: { phone_number: this.phoneNumber },
          onSuccess: this.afterCreatePin,
          onError: (error) => { this.$emit('setErrors', error) }
        })
      },

      linkAction() {
        if (this.isPinStep) {
          const pinCodeStep = this.steps[0]
          pinCodeStep.hasBeenSent = true
          //We show the button "resend" after 10 minutes
          window.setTimeout(() => {
            pinCodeStep.hasBeenSent = false
          }, 600000)
          this.resendPin()
        }
      },

      resendPin() {
        this.axiosMethod({
          methodType: 'put',
          url: `${this.pinUrl}/${this.pinId}`,
          onSuccess: this.afterResendPinSuccess,
          onError: this.afterResendPinError
        })
      },

      resetLogin() {
        this.$emit('updateData', 'phoneNumber', null)
        this.$emit('updateData', 'type', 'sign-in')
      },

      setPassword() {
        this.loading = true
        this.axiosMethod({
          methodType: 'patch',
          url: `${this.baseUrl}/member_passwords/${this.phoneNumber}`,
          params: { password: this.password, access_code_id: this.pinId },
          onSuccess: this.afterSetPasswordSuccess,
          onError: this.afterSetPasswordError
        })
      },

      setTimer() {
        let timer = 600
        clearInterval(this.pageTimer)
        this.pageTimer = setInterval(() => {
          if (timer <= 0) {
            this.resetLogin()
            this.$emit('setPageAlert', 'access PIN expired, please restart')
          }
          timer--
        }, 1000)
      },

      submitAction() {
        this.isPinStep ? this.submitPin() : this.confirmNewPassword()
      },

      submitPin() {
        if (!this.pin) { this.$emit('setPageAlert', 'must enter valid access PIN'); return }

        this.loading = true
        this.axiosMethod({
          url: `${this.pinUrl}/${this.pinId}?access_code=${this.pin}`,
          onSuccess: this.afterSubmitPinSuccess,
          onError: this.afterSubmitPinError
        })
      },

      updateData(field, value) {
        this[field] = value
      },
    },

    created() {
      this.configureSteps()
      this.createPin()
    },

    computed: {
      isPinStep: function() {
        return this.currentStep === 'pin'
      },

      pinUrl: function() {
        return `${this.baseUrl}/member_access_codes`
      }
    }
}
</script>

